/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import {
  TimelineBackground,
  TimelineTimeBar,
  WarningBlocksLayer,
} from './WarningsTimelineLayers';
import {
  EDGE_PADDING,
  getTimeFromX,
  getTimelineX,
} from './warningsTimelineUtils';
import type { Warning } from '../../store/warningsForm/types';

export interface WarningsTimelineProps {
  warnings?: Warning[];
  hoveredWarningId?: string;
  onHoverWarning?: (warningId?: string) => void;
}

const WARNING_BLOCK_HEIGHT = 34;
const DEFAULT_VISIBLE_ROWS = 10;
const TIMELINE_BAR_HEIGHT = 40;
const TIMELINE_TOP_PADDING = 8;

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const DEFAULT_VISIBLE_HOURS = 4 * 24;

export const WarningsTimeline: React.FC<WarningsTimelineProps> = ({
  warnings,
  hoveredWarningId,
  onHoverWarning,
}) => {
  const timelineWarnings = React.useMemo(
    () =>
      warnings?.filter(
        (warning) =>
          Boolean(warning.warningDetail.validFrom) &&
          Boolean(warning.warningDetail.validUntil),
      ) ?? [],
    [warnings],
  );

  const warningsAreaRef = React.useRef<HTMLDivElement | null>(null);
  const timeAxisRef = React.useRef<HTMLDivElement>(null);
  const [scrollContainer, setScrollContainer] =
    React.useState<HTMLDivElement | null>(null);
  const [containerSize, setContainerSize] = React.useState({
    width: 0,
    height: 0,
  });

  const handleWarningsAreaRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      warningsAreaRef.current = node;
      setScrollContainer(node);
    },
    [],
  );

  React.useEffect(() => {
    if (!warningsAreaRef.current) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const nextSize = {
          width: Math.round(entry.contentRect.width),
          height: Math.round(entry.contentRect.height),
        };

        setContainerSize((currentSize) =>
          currentSize.width === nextSize.width &&
          currentSize.height === nextSize.height
            ? currentSize
            : nextSize,
        );
      }
    });

    resizeObserver.observe(warningsAreaRef.current);
    return (): void => resizeObserver.disconnect();
  }, []);

  const [now, setNow] = React.useState(() => new Date());
  const defaultTimelineRange = React.useMemo(() => {
    const snapshotNow = new Date();
    return {
      end: new Date(snapshotNow.getTime() + 3 * MS_PER_DAY),
      start: new Date(snapshotNow.getTime() - MS_PER_DAY),
    };
  }, []);
  const isApplyingScrollRef = React.useRef(false);
  const hasUserScrolledRef = React.useRef(false);
  const visibleStartTimeRef = React.useRef(defaultTimelineRange.start);

  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), MS_PER_HOUR / 60);
    return (): void => clearInterval(id);
  }, []);

  const { timelineStart, timelineEnd } = React.useMemo(() => {
    if (!timelineWarnings.length) {
      return {
        timelineEnd: defaultTimelineRange.end,
        timelineStart: defaultTimelineRange.start,
      };
    }

    let warningMin = Number.POSITIVE_INFINITY;
    let warningMax = Number.NEGATIVE_INFINITY;

    for (const warning of timelineWarnings) {
      const { validFrom, validUntil } = warning.warningDetail;
      if (validFrom) {
        warningMin = Math.min(warningMin, new Date(validFrom).getTime());
      }
      if (validUntil) {
        warningMax = Math.max(warningMax, new Date(validUntil).getTime());
      }
    }

    const warningStartDate = new Date(warningMin);
    warningStartDate.setUTCMinutes(0, 0, 0);
    const flooredHour =
      warningStartDate.getUTCHours() - (warningStartDate.getUTCHours() % 3) - 3;
    warningStartDate.setUTCHours(flooredHour);

    const minTime = Math.min(
      defaultTimelineRange.start.getTime(),
      warningStartDate.getTime(),
    );
    const maxTime = Math.max(defaultTimelineRange.end.getTime(), warningMax);

    return {
      timelineEnd: new Date(maxTime),
      timelineStart: new Date(minTime),
    };
  }, [defaultTimelineRange, timelineWarnings]);

  const totalMs = timelineEnd.getTime() - timelineStart.getTime();
  const totalHours = totalMs / MS_PER_HOUR;
  const defaultVisibleContentWidth = Math.max(
    1,
    containerSize.width - 2 * EDGE_PADDING,
  );
  const pixelsPerHour = defaultVisibleContentWidth / DEFAULT_VISIBLE_HOURS;
  const contentWidth = Math.max(
    defaultVisibleContentWidth,
    totalHours * pixelsPerHour,
  );
  const fullWidth = contentWidth + 2 * EDGE_PADDING;

  const setScrollPositionForTime = React.useCallback(
    (targetTime: Date): void => {
      if (!warningsAreaRef.current) {
        return;
      }

      const scrollLeft = Math.max(
        0,
        getTimelineX(targetTime, timelineStart, timelineEnd, contentWidth) -
          EDGE_PADDING,
      );
      const maxScrollLeft = Math.max(
        0,
        fullWidth - warningsAreaRef.current.clientWidth,
      );
      const nextScrollLeft = Math.min(scrollLeft, maxScrollLeft);

      isApplyingScrollRef.current = true;
      warningsAreaRef.current.scrollLeft = nextScrollLeft;
      if (timeAxisRef.current) {
        timeAxisRef.current.scrollLeft = nextScrollLeft;
      }
      window.requestAnimationFrame(() => {
        isApplyingScrollRef.current = false;
      });
    },
    [contentWidth, fullWidth, timelineEnd, timelineStart],
  );

  React.useLayoutEffect(() => {
    setScrollPositionForTime(
      hasUserScrolledRef.current
        ? visibleStartTimeRef.current
        : defaultTimelineRange.start,
    );
  }, [defaultTimelineRange.start, setScrollPositionForTime]);

  const handleWarningsScroll = React.useCallback(() => {
    if (!warningsAreaRef.current || !timeAxisRef.current) {
      return;
    }

    timeAxisRef.current.scrollLeft = warningsAreaRef.current.scrollLeft;
    visibleStartTimeRef.current = getTimeFromX(
      warningsAreaRef.current.scrollLeft + EDGE_PADDING,
      timelineStart,
      timelineEnd,
      contentWidth,
    );

    if (!isApplyingScrollRef.current) {
      hasUserScrolledRef.current = true;
    }
  }, [contentWidth, timelineEnd, timelineStart]);

  const timelineRowsHeight = Math.max(
    timelineWarnings.length * WARNING_BLOCK_HEIGHT + TIMELINE_TOP_PADDING,
    DEFAULT_VISIBLE_ROWS * WARNING_BLOCK_HEIGHT + TIMELINE_TOP_PADDING,
  );
  const timelineCanvasHeight = Math.max(
    containerSize.height,
    timelineRowsHeight,
  );

  const warningBlocks = React.useMemo(() => {
    return timelineWarnings.map((warning, index) => {
      const { warningDetail } = warning;
      const validFrom = new Date(warningDetail.validFrom as string);
      const validUntil = new Date(warningDetail.validUntil as string);

      const left = getTimelineX(
        validFrom,
        timelineStart,
        timelineEnd,
        contentWidth,
      );
      const right = getTimelineX(
        validUntil,
        timelineStart,
        timelineEnd,
        contentWidth,
      );

      return {
        key: warning.id ?? `${validFrom.toISOString()}-${index}`,
        left,
        top: TIMELINE_TOP_PADDING + index * WARNING_BLOCK_HEIGHT,
        warning,
        width: Math.max(0, right - left),
      };
    });
  }, [contentWidth, timelineEnd, timelineStart, timelineWarnings]);

  const [hoverTimelineX, setHoverTimelineX] = React.useState<number | null>(
    null,
  );

  const hoverTime =
    hoverTimelineX !== null
      ? `${getTimeFromX(
          hoverTimelineX,
          timelineStart,
          timelineEnd,
          contentWidth,
        ).toLocaleString('en-GB', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'UTC',
        })} UTC`
      : '';

  const handleTimeAxisMouseMove = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      if (event.clientY - rect.top < 10) {
        setHoverTimelineX(null);
        return;
      }

      setHoverTimelineX(
        event.clientX - rect.left + (timeAxisRef.current?.scrollLeft ?? 0),
      );
    },
    [],
  );

  const handleTimeAxisMouseLeave = React.useCallback(() => {
    setHoverTimelineX(null);
  }, []);

  const currentTimeX = getTimelineX(
    now,
    timelineStart,
    timelineEnd,
    contentWidth,
  );
  const showCurrentTime =
    now.getTime() >= timelineStart.getTime() &&
    now.getTime() <= timelineEnd.getTime();

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'geowebColors.background.surface',
      }}
    >
      <Box
        data-testid="warnings-timeline-scroll-container"
        onScroll={handleWarningsScroll}
        ref={handleWarningsAreaRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          scrollbarGutter: 'stable',
          minHeight: 0,
        }}
      >
        <Box sx={{ width: fullWidth, position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              height: timelineCanvasHeight,
            }}
          >
            <TimelineBackground
              contentWidth={contentWidth}
              scrollContainer={scrollContainer}
              timelineEnd={timelineEnd}
              timelineStart={timelineStart}
            />

            {showCurrentTime ? (
              <Box
                sx={{
                  position: 'absolute',
                  left: currentTimeX,
                  top: 0,
                  bottom: 0,
                  width: '3px',
                  bgcolor: '#0051DD',
                  zIndex: 10,
                }}
              />
            ) : null}

            <WarningBlocksLayer
              hoveredWarningId={hoveredWarningId}
              onHoverWarning={onHoverWarning}
              warningBlocks={warningBlocks}
            />
          </Box>
        </Box>
      </Box>

      <Tooltip
        enterDelay={150}
        followCursor
        leaveDelay={0}
        placement="top"
        slotProps={{
          popper: {
            sx: {
              pointerEvents: 'none',
            },
          },
        }}
        title={hoverTime}
      >
        <Box
          onMouseLeave={handleTimeAxisMouseLeave}
          onMouseMove={handleTimeAxisMouseMove}
          ref={timeAxisRef}
          sx={{
            height: TIMELINE_BAR_HEIGHT,
            overflow: 'hidden',
            borderTop: '1px solid #ccc',
          }}
        >
          <TimelineTimeBar
            contentWidth={contentWidth}
            fullWidth={fullWidth}
            scrollContainer={scrollContainer}
            timelineEnd={timelineEnd}
            timelineStart={timelineStart}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};
